import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollText, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const CATEGORIES = [
  'Apparel & Accessories',
  'Books & Media',
  'Health & Wellness',
  'Professional Services',
  'Christian Tech/SaaS',
  'Art & Decor',
  'Other',
];

const OPERATIONAL_YEARS = [
  'Less than 1 year',
  '1–3 years',
  '3–5 years',
  '5+ years',
];

const FULFILLMENT_WINDOWS = [
  'Within 24–48 hours',
  '3–5 business days',
  '5–10 business days',
  'Made-to-order (varies)',
];

export default function VendorApplicationPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    legal_business_name: '',
    operating_brand_name: '',
    primary_contact_name: '',
    contact_email: '',
    phone_number: '',
    website_or_social: '',
    business_category: '',
    other_category: '',
    years_operational: '',
    sourcing_description: '',
    fulfillment_window: '',
    marketplace_ministry: '',
    biblical_principles_in_practice: '',
    community_giving: '',
    affirms_vision_statement: false,
    affirms_covenant: false,
  });

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.affirms_vision_statement || !form.affirms_covenant) {
      toast.error('Please check both affirmation boxes to submit.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/vendors/applications`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 max-w-2xl mx-auto px-6 md:px-12 py-16 w-full">
          <Card className="border-primary/10 text-center" data-testid="application-success">
            <CardContent className="p-10">
              <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-5" />
              <h1 className="text-3xl font-heading font-semibold mb-3">Application Received 🙏</h1>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Thank you for stepping forward. The Christ Kingdom team will review your application
                and respond within <strong>2-5 business days</strong>. Approved vendors receive a
                personal onboarding email with next steps, including their Founding Vendor benefits.
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <Button onClick={() => navigate('/')}>Back to Home</Button>
                <Link to="/vendor/covenant">
                  <Button variant="outline">Review our Covenant</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-6 md:px-12 py-12 w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
            <ScrollText className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight">
            Vendor Application
          </h1>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            Thank you for your interest in joining our collective of faith-driven entrepreneurs.
            Please complete this application thoroughly so we can evaluate both your business
            operations and your alignment with our core marketplace vision.
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            Already read our <Link to="/vendor/covenant" className="text-primary underline">Partnership Covenant</Link>?
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="vendor-application-form">
          {/* Section 1 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Section 1: General Business Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Legal Business Name *</Label>
                <Input required value={form.legal_business_name} onChange={(e) => updateField('legal_business_name', e.target.value)} data-testid="legal-name" />
              </div>
              <div>
                <Label>Operating / Brand Name (if different)</Label>
                <Input value={form.operating_brand_name} onChange={(e) => updateField('operating_brand_name', e.target.value)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Primary Contact Name &amp; Title *</Label>
                  <Input required value={form.primary_contact_name} onChange={(e) => updateField('primary_contact_name', e.target.value)} />
                </div>
                <div>
                  <Label>Email Address *</Label>
                  <Input type="email" required value={form.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} data-testid="contact-email" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Phone Number *</Label>
                  <Input required value={form.phone_number} onChange={(e) => updateField('phone_number', e.target.value)} />
                </div>
                <div>
                  <Label>Website / Social Media Links</Label>
                  <Input placeholder="https://… or @handle" value={form.website_or_social} onChange={(e) => updateField('website_or_social', e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Section 2: Business Logistics &amp; Readiness</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label>Business Category *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {CATEGORIES.map((cat) => (
                    <label key={cat} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        value={cat}
                        checked={form.business_category === cat}
                        onChange={(e) => updateField('business_category', e.target.value)}
                        required
                      />
                      {cat}
                    </label>
                  ))}
                </div>
                {form.business_category === 'Other' && (
                  <Input
                    className="mt-2"
                    placeholder="Please specify"
                    value={form.other_category}
                    onChange={(e) => updateField('other_category', e.target.value)}
                  />
                )}
              </div>
              <div>
                <Label>How long has your business been operational? *</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  {OPERATIONAL_YEARS.map((y) => (
                    <label key={y} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="years"
                        value={y}
                        checked={form.years_operational === y}
                        onChange={(e) => updateField('years_operational', e.target.value)}
                        required
                      />
                      {y}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>How are your products manufactured or sourced? Briefly describe your supply chain and commitment to ethical practices. *</Label>
                <Textarea
                  required
                  rows={4}
                  value={form.sourcing_description}
                  onChange={(e) => updateField('sourcing_description', e.target.value)}
                />
              </div>
              <div>
                <Label>Current fulfillment window (avg. order placement → ship time) *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {FULFILLMENT_WINDOWS.map((w) => (
                    <label key={w} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="fulfill"
                        value={w}
                        checked={form.fulfillment_window === w}
                        onChange={(e) => updateField('fulfillment_window', e.target.value)}
                        required
                      />
                      {w}
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 3 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Section 3: Kingdom Alignment &amp; Marketplace Ministry</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>How do you view your business as an extension of Marketplace Ministry? *</Label>
                <Textarea required rows={4} value={form.marketplace_ministry} onChange={(e) => updateField('marketplace_ministry', e.target.value)} />
              </div>
              <div>
                <Label>How does your company actively integrate Biblical principles (e.g., integrity, fair wages, radical generosity) into daily operations? *</Label>
                <Textarea required rows={4} value={form.biblical_principles_in_practice} onChange={(e) => updateField('biblical_principles_in_practice', e.target.value)} />
              </div>
              <div>
                <Label>Does your business allocate a portion of profits, products, or time to support community transformation, local ministries, or global missions? If so, please share briefly.</Label>
                <Textarea rows={3} value={form.community_giving} onChange={(e) => updateField('community_giving', e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {/* Section 4 */}
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader>
              <CardTitle className="text-lg">Section 4: Affirmation &amp; Submission</CardTitle>
              <CardDescription>Please confirm both before submitting.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={form.affirms_vision_statement}
                  onChange={(e) => updateField('affirms_vision_statement', e.target.checked)}
                  data-testid="affirm-vision"
                />
                <span className="text-sm leading-relaxed">
                  <strong>I affirm:</strong> I have read the Christ Kingdom Vision Statement. I certify that all
                  information provided is accurate and that my business operates in a manner that honors God and
                  reflects high-quality stewardship.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={form.affirms_covenant}
                  onChange={(e) => updateField('affirms_covenant', e.target.checked)}
                  data-testid="affirm-covenant"
                />
                <span className="text-sm leading-relaxed">
                  I have read and agree to the{' '}
                  <Link to="/vendor/covenant" target="_blank" className="text-primary underline font-medium">
                    Partnership Covenant &amp; Code of Honor
                  </Link>{' '}
                  and the platform&apos;s{' '}
                  <Link to="/terms" target="_blank" className="text-primary underline font-medium">
                    Terms of Service
                  </Link>.
                </span>
              </label>
            </CardContent>
          </Card>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={submitting || !form.affirms_vision_statement || !form.affirms_covenant}
            data-testid="submit-application"
          >
            {submitting ? 'Submitting your application…' : 'Submit Application →'}
          </Button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
